import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, Clock, Send, Loader2, MessageCircle } from "lucide-react";
import { DOCTOR } from "../data/content";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contacto() {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", asunto: "", mensaje: "" });
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Completa los campos obligatorios");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setForm({ nombre: "", email: "", telefono: "", asunto: "", mensaje: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Error al enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const whatsappMsg = encodeURIComponent("Hola Dr. César, me gustaría agendar una consulta.");
  const whatsappUrl = `https://wa.me/52${DOCTOR.whatsapp.replace(/\D/g, "")}?text=${whatsappMsg}`;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
      <SEO
        title="Contacto"
        description="Agenda tu consulta con el Dr. César S. Martínez Maldonado, psiquiatra en Xalapa, Veracruz. Llámanos o envía un mensaje. Atención confidencial y profesional."
        canonical="/contacto"
      />
      <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-700 transition mb-8 text-sm">
        <ArrowLeft size={16} /> Volver al inicio
      </Link>

      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 text-cyan-700 text-sm font-medium mb-4">
          <MessageCircle size={16} /> Contacto
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
          Agenda tu consulta
        </h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
          Estamos aquí para escucharte. Contáctanos por el medio que prefieras.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Contact info */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-gradient-to-br from-cyan-600 to-teal-700 rounded-2xl p-7 text-white shadow-xl">
            <h3 className="text-xl font-bold mb-5">Consultorio principal</h3>
            <div className="space-y-4">
              <InfoRow icon={MapPin} title="Dirección">
                <a href="https://maps.google.com/?q=19.5487041,-96.92388" target="_blank" rel="noopener noreferrer" className="hover:underline">{DOCTOR.address}</a>
              </InfoRow>
              <InfoRow icon={Phone} title="Teléfono fijo">
                <a href={`tel:${DOCTOR.phone.replace(/\s/g, "")}`} className="hover:underline">{DOCTOR.phone}</a>
              </InfoRow>
              <InfoRow icon={Phone} title="Celular / WhatsApp">
                <a href={`tel:${DOCTOR.mobile.replace(/\s/g, "")}`} className="hover:underline">{DOCTOR.mobile}</a>
              </InfoRow>
            </div>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="text-cyan-600" size={18} /> Segunda sede
            </h3>
            <div className="space-y-2 text-sm text-slate-600">
              <p className="font-medium text-slate-900">Hospital Ángel</p>
              <p>Martínez de la Torre, Veracruz</p>
              <a href={`tel:${DOCTOR.phoneMartinez.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-cyan-700 font-medium hover:text-cyan-900">
                <Phone size={14} /> {DOCTOR.phoneMartinez}
              </a>
            </div>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Mail className="text-cyan-600" size={18} /> Correos electrónicos
            </h3>
            <div className="space-y-2 text-sm">
              <a href={`mailto:${DOCTOR.email}`} className="text-slate-600 hover:text-cyan-700 break-all block">{DOCTOR.email}</a>
            </div>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Clock className="text-cyan-600" size={18} /> Horarios
            </h3>
            <div className="space-y-1.5 text-sm text-slate-600">
              <p><span className="font-medium text-slate-800">Lun-Vie:</span> 9:00 - 19:00</p>
              <p><span className="font-medium text-slate-800">Sábado:</span> 10:00 - 14:00</p>
              <p className="text-xs text-slate-500 mt-2">* Consulta con cita previa</p>
            </div>
          </div>

          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
             className="block w-full bg-green-500 hover:bg-green-600 text-white rounded-2xl p-5 shadow-md text-center font-semibold transition-all hover:scale-[1.02]">
            <div className="flex items-center justify-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              Enviar mensaje por WhatsApp
            </div>
          </a>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          <form onSubmit={submit} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Envíanos un mensaje</h2>
            <p className="text-slate-600 mb-6">Responderemos en las próximas horas hábiles.</p>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <Field label="Nombre completo *" value={form.nombre} onChange={handle("nombre")} required />
              <Field label="Correo electrónico *" type="email" value={form.email} onChange={handle("email")} required />
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <Field label="Teléfono" type="tel" value={form.telefono} onChange={handle("telefono")} />
              <Field label="Asunto" value={form.asunto} onChange={handle("asunto")} />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Mensaje *</label>
              <textarea
                value={form.mensaje}
                onChange={handle("mensaje")}
                required
                rows={6}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none transition"
                placeholder="Cuéntanos cómo podemos ayudarte..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:shadow-lg text-white font-semibold py-3.5 px-6 rounded-xl transition-all hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="animate-spin" size={18} /> Enviando...</>
              ) : (
                <><Send size={18} /> Enviar mensaje</>
              )}
            </button>

            <p className="text-xs text-slate-500 mt-4 text-center">
              Tu información es confidencial y solo será utilizada para responder tu consulta.
            </p>
          </form>
        </div>
      </div>

      {/* Mapa */}
      <div className="mt-10">
        <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <MapPin className="text-cyan-600" size={20} /> Cómo llegar
        </h2>
        <div className="rounded-2xl overflow-hidden shadow-md border border-slate-100 w-full h-80">
          <iframe
            title="Ubicación consultorio Dr. César Martínez"
            src="https://maps.google.com/maps?q=19.5487041,-96.92388&z=17&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <input
        {...props}
        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs text-cyan-100 uppercase tracking-wider font-medium">{title}</div>
        <div className="text-white font-medium">{children}</div>
      </div>
    </div>
  );
}
